import {FC, ReactElement} from "react";
import PizzaCafe from "../games/PizzaCafe";
import CandyBlast from "../games/CandyBlast";
import StackTheBurger from "../games/StackTheBurger";
import RallyChampion from "../games/RallyChampion";
import InTheWeeds from "../games/InTheWeeds";

export interface GoVipLevelsCardData {
    level: number;
    prize: string;
    description: string;
    xpRequired?: number;
    fuelBonus?: number;
    imageUrl?: string;
}

/**
 * Maps a dynamic LevelConfig API response to the card data shape.
 * fuelBonusAmount from the API drives the "Bonus $X FUEL Tokens" card row.
 */
export function mapLevelConfigToCardData(config: {
    level: number;
    name: string;
    description?: string;
    xpRequired?: number;
    fuelBonusAmount?: number;
    imageAsset?: { cdnUrl?: string };
}): GoVipLevelsCardData {
    return {
        level: config.level,
        prize: config.name,
        description: config.description || "",
        xpRequired: config.xpRequired,
        fuelBonus: config.fuelBonusAmount,
        imageUrl: config.imageAsset?.cdnUrl,
    };
}

export const getGoVipLevelsText = (fuelStatus: boolean) => {
    const getFuelTokenText = (amount: number, title: boolean = true) => {
        if (title) {
            return fuelStatus ? `$${amount} ${import.meta.env.VITE_TOKEN_NAME} Tokens` : "N/A";
        }
        return fuelStatus
            ? `Earn $${amount} worth of ${
                import.meta.env.VITE_TOKEN_NAME
            } Tokens that you can spend in the LootShop on gift cards and rewards!`
            : "N/A";
    };
    return [
        {
            level: 1,
            prize: "$5 OFF Promo Code",
            description: "Earn $5 OFF discount code you can use in the LootShop on gift cards and merchandise",
        },
        {
            level: 2,
            prize: "Infinite Promo Access Pass",
            description: "Get exclusive access to LootShop promotions and special rewards every time we roll out new deals",
        },
        {
            level: 3,
            prize: "Loot Box #1",
            description: "You may be able to unlock FUEL rewards, free trips, electronics, or other exclusive prizes",
        },
        {
            level: 4,
            prize: "Devour T-Shirt",
            description: "Get a free exclusive T-shirt from Devour! It's your badge in our community",
        },
        {
            level: 5,
            prize: "$10 OFF Promo Code",
            description: "Earn $10 OFF discount code you can use in the LootShop on gift cards and merchandise",
        },
        {
            level: 6,
            prize: "Loot Box #2",
            description: "You may be able to unlock FUEL rewards, free trips, electronics, or other exclusive prizes",
        },
        {
            level: 7,
            prize: "$10 Devour LootShop Credit",
            description: "Get a $10 credit that you can spend in the LootShop",
        },
        {
            level: 8,
            prize: "Devour Sweatshirt",
            description: "Get a free exclusive sweatshirt from Devour! It's your badge in our community",
        },
        {
            level: 9,
            prize: getFuelTokenText(20),
            description: getFuelTokenText(20, false),
        },
        {
            level: 10,
            prize: "$20 OFF Promo Code",
            description: "Earn $20 OFF discount code you can use in the LootShop on gift cards and merchandise",
        },
        {
            level: 11,
            prize: "Loot Box #3",
            description: "You may be able to unlock FUEL rewards, free trips, electronics, or other exclusive prizes",
        },
        {
            level: 12,
            prize: "$20 Devour LootShop Credit",
            description: "Get a $20 credit that you can spend in the LootShop",
        },
        {
            level: 13,
            prize: "DevourPlay Cap",
            description: "Get a free exclusive DevourPlay cap from Devour! It's your badge in our community.",
        },
        {
            level: 14,
            prize: "$30 Off Promo Code",
            description: "Earn $30 OFF discount code you can use in the LootShop on gift cards and merchandise",
        },
        {
            level: 15,
            prize: getFuelTokenText(25),
            description: getFuelTokenText(25, false),
        },
        {
            level: 16,
            prize: "Loot Box #4",
            description: "You may be able to unlock FUEL rewards, free trips, electronics, or other exclusive prizes",
        },
        {
            level: 17,
            prize: "$30 Devour LootShop Credit",
            description: "Get a $30 credit that you can spend in the LootShop",
        },
        {
            level: 18,
            prize: "$40 OFF Promo",
            description: "Earn $40 OFF discount you can use in the LootShop on gift cards and merchandise",
        },
        {
            level: 19,
            prize: getFuelTokenText(30),
            description: getFuelTokenText(30, false),
        },
        {
            level: 20,
            prize: "DevourPlay Gamer Chef Bobblehead",
            description: "Get a free exclusive DevourPlay Chef bobblehead from Devour!",
        },
    ];
};

export interface GoVipGameData {
    game: string;
    description: string;
    levelsData: Array<string>;
    thumbnailUrl?: string;
    reward: string;
    rewardSubtitle: string;
}

export interface GoVipGameThumbnailData {
    requiredLevel: number;
    gameContainer: FC;
    thumbnailUrl?: string;
    title: string;
    levelDetailsModal?: ReactElement;
    description: string;
    gameData?: GoVipGameData;

}

export const pizzaCafeLevelDetails: GoVipGameData = {
    game: "Pizza Cafe",
    thumbnailUrl: `${import.meta.env.VITE_CDN_URL}/games/pizza-cafe/media/graphics/promo/screenshots/1.jpg`,
    description: "Serve orders fast and stack XP.",
    reward: "+100",
    rewardSubtitle: "XP",
    levelsData: [
        "Complete Level 1",
        "Complete Level 2",
        "Complete Level 3",
        "Complete Level 4",
        "Complete Level 5",
        "Complete Level 6",
        "Complete Level 7",
        "Complete Level 8",
        "Complete Level 9",
        "Complete Level 10",
        "Complete Level 11",
        "Complete Level 12",
    ],
};

export const candyBlastLevelDetails: GoVipGameData = {
    game: "Candy Blast",
    thumbnailUrl: `${import.meta.env.VITE_CDN_URL}/games/candy-blast/media/graphics/promo/screenshots/3.png`,
    description: "Match candies, clear levels, earn XP.",
    reward: "+35",
    rewardSubtitle: "XP",
    levelsData: [
        "Complete Level 1",
        "Complete Level 2",
        "Complete Level 3",
        "Complete Level 4",
        "Complete Level 5",
        "Complete Level 6",
        "Complete Level 7",
        "Complete Level 8",
        "Complete Level 9",
        "Complete Level 10",
        "Complete Level 11",
        "Complete Level 12",
        "Complete Level 13",
        "Complete Level 14",
        "Complete Level 15",
        "Complete Level 16",
        "Complete Level 17",
        "Complete Level 18",
        "Complete Level 19",
        "Complete Level 20",
        "Complete Level 21",
        "Complete Level 22",
        "Complete Level 23",
        "Complete Level 24",
        "Complete Level 25",
        "Complete Level 26",
        "Complete Level 27",
        "Complete Level 28",
        "Complete Level 29",
        "Complete Level 30",
    ],
};

export const stackTheBurgerLevelDetails: GoVipGameData = {
    game: "Stack the Burger",
    thumbnailUrl: `${import.meta.env.VITE_CDN_URL}/games/stack-the-burger-v2/media/graphics/promo/screenshots/screenshot6.jpg`,
    description: "Build tall burgers without toppling.",
    reward: "+125",
    rewardSubtitle: "XP",
    levelsData: [
        "Earn $200 in game",
        "Earn $400 in game",
        "Earn $600 in game",
        "Earn $800 in game",
        "Earn $1000 in game",
        "Earn $1200 in game",
        "Earn $1400 in game",
        "Earn $1600 in game",
        "Earn $1800 in game",
        "Earn $2000 in game",
    ],
};

export const rallyChampionLevelDetails: GoVipGameData = {
    game: "Rally Champion",
    thumbnailUrl: `${import.meta.env.VITE_CDN_URL}/games/rally-champion/media/graphics/promo/screenshots/4.jpg`,
    description: "Win 10 races to claim rewards.",
    reward: "+150",
    rewardSubtitle: "XP",
    levelsData: ["Win 1st Place"],
};

export const inTheWeedsLevelDetails: GoVipGameData = {
    game: "In the Weeds",
    thumbnailUrl: `${import.meta.env.VITE_CDN_URL}/games/in-the-weeds/media/graphics/promo/screenshots/6.png`,
    description: "Survive kitchen chaos. Earn big.",
    reward: "+★",
    rewardSubtitle: "XP",
    levelsData: [
        "Complete Level 1",
        "Complete Level 2",
        "Complete Level 3",
        "Complete Level 4",
        "Complete Level 5",
        "Complete Level 6",
        "Complete Level 7",
        "Complete Level 8",
        "Complete Level 9",
        "Complete Level 10",
        "Complete Level 11",
        "Complete Level 12",
        "Complete Level 13",
        "Complete Level 14",
        "Complete Level 15",
        "Complete Level 16",
        "Complete Level 17",
        "Complete Level 18",
    ],
};

export const GameThumbnailData: Array<GoVipGameThumbnailData> = [
    {
        requiredLevel: 1,
        gameContainer: PizzaCafe,
        thumbnailUrl: `${import.meta.env.VITE_CDN_URL}/games/pizza-cafe/media/graphics/promo/screenshots/1.jpg`,
        title: "Pizza Café",
        description: "Serve orders fast and stack XP.",
        gameData: pizzaCafeLevelDetails,
    },
    {
        requiredLevel: 1,
        gameContainer: CandyBlast,
        thumbnailUrl: `${import.meta.env.VITE_CDN_URL}/games/candy-blast/media/graphics/promo/screenshots/3.png`,
        title: "Candy Blast",
        description: "Match candies, clear levels, earn XP.",
        gameData: candyBlastLevelDetails,
    },
    {
        requiredLevel: 4,
        gameContainer: StackTheBurger,
        thumbnailUrl: `${import.meta.env.VITE_CDN_URL}/games/stack-the-burger-v2/media/graphics/promo/screenshots/screenshot6.jpg`,
        title: "Stack the Burger",
        description: "Build tall burgers without toppling.",
        gameData: stackTheBurgerLevelDetails,
    },
    {
        requiredLevel: 7,
        gameContainer: RallyChampion,
        thumbnailUrl: `${import.meta.env.VITE_CDN_URL}/games/rally-champion/media/graphics/promo/screenshots/4.jpg`,
        title: "Rally Champion",
        description: "Win 10 races to claim rewards.",
        gameData: rallyChampionLevelDetails,
    },
    {
        requiredLevel: 9,
        gameContainer: InTheWeeds,
        thumbnailUrl: `${import.meta.env.VITE_CDN_URL}/games/in-the-weeds/media/graphics/promo/screenshots/6.png`,
        title: "In the Weeds",
        description: "Survive kitchen chaos. Earn big.",
        gameData: inTheWeedsLevelDetails,
    },
];
